/**
 * jrVoice — tiny wrapper around the browser SpeechSynthesis API so we
 * can read LemonBot Jr.'s text aloud for early readers. No backend,
 * no API keys, works offline once the voice is loaded.
 *
 * Preference stored in localStorage so students can toggle voice on/off
 * once and we respect it across the whole demo.
 */

const VOICE_PREF_KEY = "lemonade_jr_voice_on_v1";

function canSpeak() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function isVoiceOn() {
  if (typeof window === "undefined") return false;
  try {
    // Default ON — kids should hear LemonBot Jr. on first visit.
    const v = window.localStorage.getItem(VOICE_PREF_KEY);
    return v === null ? true : v === "1";
  } catch {
    return true;
  }
}

export function setVoiceOn(on) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VOICE_PREF_KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (!on && canSpeak()) {
    try {
      window.speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
  }
}

/** Pick a friendly voice — prefer a female US English voice if available. */
function pickVoice() {
  if (!canSpeak()) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;
  const preferred = voices.find(
    (v) =>
      /en(-|_)US/i.test(v.lang) &&
      /(female|samantha|victoria|karen|zira|ava|zoe)/i.test(v.name)
  );
  if (preferred) return preferred;
  const en = voices.find((v) => /^en/i.test(v.lang));
  return en || voices[0];
}

/** Speak a string. Safe no-op if the API is missing or muted. */
export function speak(text, { rate = 0.95, pitch = 1.1, volume = 1, force = false } = {}) {
  if (!text || !canSpeak()) return;
  if (!force && !isVoiceOn()) return;
  const s = window.speechSynthesis;
  try {
    s.cancel();
    const u = new window.SpeechSynthesisUtterance(String(text));
    const v = pickVoice();
    if (v) u.voice = v;
    u.rate = rate;
    u.pitch = pitch;
    u.volume = volume;
    u.lang = (v && v.lang) || "en-US";
    s.speak(u);
  } catch {
    /* ignore */
  }
}

/** Cancel any in-flight speech. */
export function stopSpeaking() {
  if (!canSpeak()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* ignore */
  }
}

/** Warm the voice list up — some browsers load voices async. */
export function primeVoices() {
  if (!canSpeak()) return;
  try {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.addEventListener?.("voiceschanged", () => {
      /* no-op — just triggers internal list hydration */
    });
  } catch {
    /* ignore */
  }
}
